import { Text, Tooltip } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { CreditFacilitySetupFooter } from "features/credit-facility/setup/CreditFacilitySetupFooter";
import { CreditFacilitySetupPageProps } from "features/credit-facility/setup/CreditFacilitySetupPageProps";
import {
  getCreditFacilityRetrieveQueryKey,
  useCreditFacilityUpsert,
} from "gen/endpoints/credit-facility/credit-facility";
import { useLenderListSuspense } from "gen/endpoints/lender/lender";
import { CreditFacilityTypeEnum } from "gen/model/creditFacilityTypeEnum";
import { CreditFacilityUpsertDataRequest } from "gen/model/creditFacilityUpsertDataRequest";
import { creditFacilityUpsertBody } from "gen/schemas/credit-facility/credit-facility";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import { FormCheckbox } from "shared/components/input/checkbox/Checkbox";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormTextInput } from "shared/components/input/text/TextInput";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { usePageWizard } from "shared/components/layout/PageWizard";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { z as zod } from "zod";

export const CreditFacilitySetupDetails: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);
  const queryClient = useQueryClient();

  const { fundFamilyId, creditFacilityId, creditFacility } =
    useOutletContext<CreditFacilitySetupPageProps>();

  const form = useForm<CreditFacilityUpsertDataRequest>({
    defaultValues: creditFacility,
    resolver: zodResolver(
      creditFacilityUpsertBody.superRefine((values, ctx) => {
        if (new Date(values.inception_date) >= new Date(values.maturity_date)) {
          ctx.addIssue({
            code: zod.ZodIssueCode.invalid_date,
            message: t("creditFacility:create.maturityDateError"),
            path: ["maturity_date"],
          });
        }
      }),
    ),
  });
  const { mutateAsync: updateCreditFacility } = useCreditFacilityUpsert();
  const { data: lenderEntitiesData } = useLenderListSuspense();
  const { navigateNext } = usePageWizard();
  return (
    <PageContent>
      <PageHeader
        workflowName={t("creditFacility:setup.title")}
        title={t("creditFacility:setup.details.title")}
      />
      <PageBody>
        <ContentBlockGroup>
          <Form
            form={form}
            onSubmit={async (data: CreditFacilityUpsertDataRequest) => {
              await updateCreditFacility({
                fundFamilyId,
                data,
              });
              await queryClient.invalidateQueries({
                queryKey: getCreditFacilityRetrieveQueryKey(
                  fundFamilyId,
                  creditFacilityId,
                ),
              });
            }}
            onSuccess={navigateNext}
          >
            <ContentBlock>
              <ContentBlockBody variant="form">
                <FormTextInput
                  name="name"
                  title={t("creditFacility:create.creditFacilityName")}
                />
                <FormSelect
                  name="facility_type"
                  title={t("creditFacility:create.facilityType")}
                >
                  {Object.values(CreditFacilityTypeEnum).map((value) => (
                    <SelectOption key={value} value={value}>
                      {t(`enums:CreditFacilityTypeEnum.${value}`)}
                    </SelectOption>
                  ))}
                </FormSelect>
                <FormSelect
                  name="lead_bank_id"
                  title={t("creditFacility:create.leadBank")}
                >
                  {lenderEntitiesData.map(({ id, legal_name }) => (
                    <SelectOption key={`lender_${id}`} value={id!.toString()}>
                      {legal_name}
                    </SelectOption>
                  ))}
                </FormSelect>
                <FormDateInput
                  name="inception_date"
                  title={t("creditFacility:create.inceptionDate")}
                />
                <FormDateInput
                  name="maturity_date"
                  title={t("creditFacility:create.maturityDate")}
                />
                <FormCheckbox
                  name="transaction_code_configs_available"
                  mb="20px"
                >
                  <Text>
                    {t("creditFacility:create.transactionCodeConfigsAvailable")}
                    <Tooltip
                      key="tooltip"
                      label={t(
                        "creditFacility:create.transactionCodeConfigsAvailableTooltip",
                      )}
                      placement="top"
                    >
                      <InfoCircleIcon
                        w="12px"
                        h="12px"
                        ml="10px"
                        color="gray.400"
                      />
                    </Tooltip>
                  </Text>
                </FormCheckbox>
                <CreditFacilitySetupFooter />
              </ContentBlockBody>
            </ContentBlock>
          </Form>
        </ContentBlockGroup>
      </PageBody>
    </PageContent>
  );
};
